import React, { useState, useRef, useMemo } from 'react';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Col,
  Form,
  Input,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import apiService from '../../../axios/apiService';
import { useUserProfile } from '../../../hooks/useUserProfile';
import ApiService from '../../../services/ApiService';
import ChangeUserPassword from './ChangeUserPassword';
import { imguser } from '../../imagepath';

const EditProfile = () => {
  const { userProfile, setUserProfile } = useUserProfile();
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    values: userProfile || {},
  });

  const [loading, setLoading] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);

  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(imguser);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedImageKey, setUploadedImageKey] = useState(null);
  const [removeProfileImage, setRemoveProfileImage] = useState(false);
  const fileInputRef = useRef(null);

  // Memoize display image to reduce render cycles
  const displayImage = useMemo(() => {
    // If user has selected a new image (has profileImage or uploadedImageKey), show preview
    if (profileImage || uploadedImageKey) {
      return profileImagePreview;
    }
    // Otherwise, show the profile image from API or fallback
    return userProfile?.profileImageUrl || imguser;
  }, [profileImagePreview, userProfile?.profileImageUrl, profileImage, uploadedImageKey]);

  const toggleChangePassword = () => {
    if (!changePasswordVisible) {
      reset();
      ApiService.forgetPassword(userProfile?.email);
    }
    setChangePasswordVisible((prevState) => !prevState);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      api.error({
        message: 'Error',
        description: 'Please select a valid image file',
        showProgress: false,
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      api.error({
        message: 'Error',
        description: 'Image size should be less than 5MB',
        showProgress: false,
      });
      return;
    }

    // Create preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    try {
      setUploadingImage(true);
      setUploadProgress(0);

      // Fetch the presigned URL from the server
      const signedUrl = await apiService.getPresignedForProfileUrl({
        fileName: file?.name,
        fileSize: file?.size,
        fileContentType: file?.type || 'image/jpeg',
      });

      if (signedUrl) {
        // Upload file using the signed URL
        const formData = new FormData();
        const fields = signedUrl?.preSigned?.fields;

        for (const key in fields) {
          if (Object.prototype.hasOwnProperty.call(fields, key)) {
            const value = fields[key];
            formData.append(key, value);
          }
        }

        formData.append('file', file);

        await apiService.uploadWithPresignedUrl(
          signedUrl?.preSigned?.url,
          formData,
          (progress) => {
            setUploadProgress(progress.percent);
          }
        );

        // Store the uploaded file info
        setProfileImage(file);
        setUploadedImageKey(signedUrl?.objectKey);
        setRemoveProfileImage(false);

        api.success({
          message: 'Success',
          description: 'Profile image uploaded successfully',
          showProgress: false,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('error on upload ', error);
      api.error({
        message: 'Error',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to upload image',
        showProgress: false,
      });

      // Reset preview on error
      setProfileImagePreview(userProfile?.profileImageUrl || imguser);
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation(); // Prevent triggering the image click handler

    setProfileImage(null);
    setProfileImagePreview(imguser);
    setUploadedImageKey(null);
    setRemoveProfileImage(true);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Prepare request data with uploaded image key or remove flag
      let requestData;

      if (uploadedImageKey) {
        // Send the objectKey of the uploaded image
        requestData = {
          firstName: data.firstName,
          lastName: data.lastName,
          nameTitle: data.nameTitle,
          profileImage: uploadedImageKey,
        };
      } else if (removeProfileImage) {
        // If user explicitly removed image, send a flag
        requestData = {
          firstName: data.firstName,
          lastName: data.lastName,
          nameTitle: data.nameTitle,
          removeProfileImage: true,
        };
      } else {
        requestData = {
          firstName: data.firstName,
          lastName: data.lastName,
          nameTitle: data.nameTitle,
        };
      }

      const response = await apiService.updateUserProfile(requestData);

      setUserProfile(response);
      setProfileImage(null);
      setProfileImagePreview(imguser);
      setUploadedImageKey(null);
      setRemoveProfileImage(false);
      setLoading(false);

      api.success({
        message: 'Success',
        description: 'Profile Updated Successfully',
        showProgress: false,
      });
    } catch (error) {
      setLoading(false);
      api.error({
        message: 'Error',
        description:
          error?.msg || error?.message || 'Failed to update profile',
        showProgress: false,
      });
    }
  };

  return (
    <>
      {contextHolder}
      <div>
        {/* Page Header */}
        <div className="page-header">
          <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12}>
              <Space>
                <Link to="#">
                  <Typography.Text>Dashboard</Typography.Text>
                </Link>
                <FeatherIcon icon="chevron-right" size={16} />
                <Typography.Text strong>Edit Profile</Typography.Text>
              </Space>
            </Col>
            <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
              <Button
                onClick={toggleChangePassword}
                type="primary"
                shape="round"
                icon={<i className="fa fa-edit" />}
              >
                {changePasswordVisible ? 'Back To Profile' : 'Change Password'}
              </Button>
            </Col>
          </Row>
        </div>

        {changePasswordVisible ? (
          <ChangeUserPassword />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Profile Image Section */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item label={<Typography.Text>Profile Image</Typography.Text>}>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'inline-block',
                        position: 'relative',
                        cursor: uploadingImage ? 'not-allowed' : 'pointer',
                      }}
                      onClick={uploadingImage ? undefined : handleImageClick}
                    >
                      <img
                        src={displayImage}
                        alt="Profile"
                        className="profile-image-edit"
                        style={{
                          opacity: uploadingImage ? 0.6 : 1,
                        }}
                      />

                      {uploadingImage && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 5,
                            textAlign: 'center',
                          }}
                        >
                          <Spin size="large" />
                          <div
                            style={{
                              marginTop: 8,
                              color: '#fff',
                              fontSize: 12,
                              fontWeight: 'bold',
                            }}
                          >
                            {uploadProgress}%
                          </div>
                        </div>
                      )}

                      {/* Remove Image Button - Top Right Corner */}
                      {(profileImage ||
                        uploadedImageKey ||
                        userProfile?.profileImage) &&
                        !uploadingImage && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm rounded-circle position-absolute top-0 end-0 d-flex justify-content-center align-items-center shadow"
                            style={{ width: '32px', height: '32px', zIndex: 10 }}
                            onClick={handleRemoveImage}
                            title="Remove image"
                          >
                            <i className="fa fa-times text-white" />
                          </button>
                        )}

                      {/* Camera Icon - Bottom Right Corner */}
                      {!uploadingImage && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm rounded-circle position-absolute bottom-0 end-0 d-flex justify-content-center align-items-center shadow"
                          style={{ width: '40px', height: '40px', zIndex: 10 }}
                          onClick={(e) => {
                            e.stopPropagation(); 
                            handleImageClick();
                          }}
                          title="Change profile image"
                        >
                          <i className="fa fa-camera text-white" />
                        </button>
                      )}
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />

                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 14, display: 'block', marginTop: 8 }}
                    >
                      Click on the image to change your profile picture
                    </Typography.Text>
                  </div>
                </Form.Item>
              </Col>
            </Row>

            {/* Profile Fields */}
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <span style={{ width: '80px' }}>
                      Title <Typography.Text type="danger">*</Typography.Text>
                    </span>
                  }
                  validateStatus={errors.nameTitle ? 'error' : ''}
                  help={errors.nameTitle ? 'Please enter Title' : ''}
                >
                  <Controller
                    name="nameTitle"
                    control={control}
                    defaultValue="Mr"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={[
                          { value: 'Mr', label: 'Mr' },
                          { value: 'Mrs', label: 'Mrs' },
                          { value: 'Ms', label: 'Ms' },
                          { value: 'Dr', label: 'Dr' },
                          { value: 'Sister', label: 'Sister' },
                        ]}
                      />
                    )}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <span style={{ width: '80px' }}>
                      First Name<Typography.Text type="danger">*</Typography.Text>
                    </span>
                  }
                  validateStatus={errors.firstName ? 'error' : ''}
                  help={errors.firstName ? 'Please enter First name' : ''}
                >
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => <Input {...field} />}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <span style={{ width: '80px' }}>
                      Last Name<Typography.Text type="danger">*</Typography.Text>
                    </span>
                  }
                  validateStatus={errors.lastName ? 'error' : ''}
                  help={errors.lastName ? 'Please enter Last name' : ''}
                >
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => <Input {...field} />}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label={<span style={{ width: '80px' }}>Email</span>}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => <Input {...field} disabled />}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button
                key="ok"
                type="primary"
                htmlType="submit"
                loading={loading || uploadingImage}
                disabled={uploadingImage}
                shape="round"
                size="large"
                style={{ paddingLeft: 40, paddingRight: 40 }}
              >
                Save
              </Button>
               <Button
                key="back"
                type="default"
                htmlType="button"
                disabled={loading || uploadingImage}
                shape="round"
                size="large"
                onClick={() => navigate('/user/profile')}
                style={{ paddingLeft: 40, paddingRight: 40, marginLeft:'4px' }}
              >
                Back to My Profile
              </Button>
            </Form.Item>
          </form>
        )}
      </div>
    </>
  );
};

export default EditProfile;
